import React, { Fragment, useEffect, useState } from 'react'
import { Box, Typography, Chip, CircularProgress, TextField, InputAdornment } from '@mui/material'
import { SharedStateType, SkuBatchInfoDto, SkuInfo } from '../../types'
import { useApi } from '../../../../hooks/useApi'
import { getSkuBatches } from '../../api'
import { Status } from '../../../../hooks/useFetchState'
import { Batch } from '../../../../interfaces/Batch'
import { SkuFefoType } from '../../../../components/_WMS/ListBatch/SkuFefoType'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { usePagination } from '../../../../hooks/usePagination'
import { Pagination } from '../../../../components/Pagination'
import { LoadingButton } from '../../../../components/_EOS/Button/LoadingButton'
import { ProductOrigin } from '../../../../interfaces/Producto/ProductOrigins'
import { formatExpirationDate } from '../../../../utils/dateHelper'
import {
  ExpiredColorStatus,
  getExpiredStatusToday
} from '../../../../utils/colors'

interface ListBatchProps {
  handleCloseModal: () => void
  batchList: Batch[]
  skuList: SkuInfo[]
  positionCode: string
  selectedSku?: SkuBatchInfoDto
  setSharedState: React.Dispatch<React.SetStateAction<SharedStateType>>
  getBackgroundStyle: (
    expirationDate: string | undefined,
    skuProductOrigin: number
  ) => React.CSSProperties
  productOrigins: ProductOrigin[] | undefined
}

const getExpirationColor = (
  expirationDate: string | null | undefined,
  days: number
): string => {
  if (!expirationDate) return '#1d4ed8'
  const status = getExpiredStatusToday(expirationDate, days)
  if (status === ExpiredColorStatus.expired) return '#dc2626'
  if (status === ExpiredColorStatus.expireSoon) return '#f59e0b'
  return '#16a34a'
}

interface BatchRowProps {
  batch: Batch
  skuFefoType: SkuFefoType
  skuProductOriginDays: number
  onClick: () => void
}

const BatchRow: React.FC<BatchRowProps> = ({
  batch,
  skuFefoType,
  skuProductOriginDays,
  onClick
}) => {
  const color = getExpirationColor(batch.expirationDate, skuProductOriginDays)

  return (
    <Box
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 0',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer'
      }}
    >
      {skuFefoType.hasBatchNumber && (
        <Typography variant='body2' style={{ fontWeight: 700, color }}>
          {batch.batchNumber}
        </Typography>
      )}
      {skuFefoType.hasExpirationDate && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Box
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color,
              flexShrink: 0
            }}
          />
          <Typography variant='body2' style={{ color, fontWeight: 600 }}>
            {batch.expirationDate
              ? formatExpirationDate(batch.expirationDate, 'dd-MM-yyyy')
              : 'N/A'}
          </Typography>
        </Box>
      )}
      {!skuFefoType.hasExpirationDate && !skuFefoType.hasBatchNumber && (
        <Typography variant='body2' style={{ fontWeight: 700, color }}>
          {batch.batchNumber}
        </Typography>
      )}
    </Box>
  )
}

export const ListBatch: React.FC<ListBatchProps> = ({
  batchList,
  skuList,
  selectedSku,
  setSharedState,
  handleCloseModal,
  positionCode,
  productOrigins
}) => {
  const { state, callApi } = useApi<ApiResponse<Batch>>()
  const pagination = usePagination(5)

  const buildFefoType = (): SkuFefoType => {
    const batchNumber = selectedSku?.hasBatch || false
    const expirationDate = selectedSku?.hasExpirationDate || false
    return {
      hasBatchNumber: batchNumber,
      hasExpirationDate: expirationDate,
      modalTitle:
        batchNumber && expirationDate
          ? 'Lotes y Vencimientos'
          : batchNumber
            ? 'Lotes'
            : 'Fechas de Vencimiento',
      modalType:
        batchNumber && expirationDate
          ? 'both'
          : batchNumber
            ? 'batch'
            : 'expiration'
    }
  }

  const skuProductOriginDays =
    productOrigins?.find(
      (el) => el.productOriginId === selectedSku?.productOriginId
    )?.expirationDays || 0

  const [skuFefoType, setSkuFefoType] = useState<SkuFefoType>(() =>
    buildFefoType()
  )
  const [isExpanded, setIsExpanded] = useState(false)
  const [searchBatch, setSearchBatch] = useState('')

  useEffect(() => {
    setSkuFefoType(buildFefoType())
  }, [selectedSku])

  const handleSelectBatch = (batchId: number, pBatchList: Batch[]) => {
    const existingSkuIndex = skuList?.findIndex(
      (item) => item.skuId === selectedSku?.skuId && item.batchId === batchId
    )
    const batch = pBatchList?.find((it) => it.batchId === batchId)

    const updatedSkuList: SkuInfo[] =
      existingSkuIndex !== undefined && existingSkuIndex >= 0
        ? skuList!.map((item, index) =>
            index === existingSkuIndex
              ? {
                  ...item,
                  checkedQty: item.checkedQty + 1,
                  validated: true,
                  skuId: selectedSku?.skuId,
                  batchId: batchId,
                  sku: selectedSku?.sku || '',
                  ean13: selectedSku?.ean13 || '',
                  batch: batch,
                  productOriginId: selectedSku?.productOriginId
                }
              : item
          )
        : [
            ...(skuList || []),
            {
              sku: selectedSku?.sku || '',
              ean13: selectedSku?.ean13 || '',
              skuId: selectedSku?.skuId,
              checkedQty: 1,
              validated: true,
              batchId: batchId,
              batch: batch,
              productOriginId: selectedSku?.productOriginId
            }
          ]

    setSharedState((prev) => ({
      ...prev,
      skuList: prev.skuList?.filter((item) => item.sku !== selectedSku?.sku)
    }))
    setSharedState((prev) => ({ ...prev, skuList: updatedSkuList }))
    handleCloseModal()
  }

  const handleLoadMoreBatch = (page = 0, pageSize = 5, batchNumber?: string) => {
    callApi(getSkuBatches(selectedSku?.skuId || 0, page, pageSize, batchNumber))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    handleLoadMoreBatch(auxPage, pageSize, searchBatch || undefined)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleSearchChange = (value: string) => {
    setSearchBatch(value)
  }

  const handleSearchSubmit = () => {
    pagination.setPagination(0, pagination.pageSize)
    handleLoadMoreBatch(0, pagination.pageSize, searchBatch || undefined)
  }

  const handleExpand = () => {
    setIsExpanded(true)
    setSearchBatch('')
    handleLoadMoreBatch(0, pagination.pageSize)
  }

  const handleCollapse = () => {
    setIsExpanded(false)
    setSearchBatch('')
  }

  const existingBatchIds = new Set(batchList.map((b) => b.batchId))
  const uniqueApiItems = (state.data?.items || []).filter(
    (b) => !existingBatchIds.has(b.batchId)
  )
  const hasNoMoreBatches =
    state.status === Status.Success && uniqueApiItems.length === 0
  const adjustedTotalItems = searchBatch
    ? state.data?.totalItems || 0
    : Math.max(0, (state.data?.totalItems || 0) - existingBatchIds.size)

  const showHeaders = skuFefoType.hasBatchNumber || skuFefoType.hasExpirationDate

  return (
    <Fragment>
      {/* Título */}
      <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 12 }}>
        Seleccionar Lote
      </Typography>

      {/* Info chips */}
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {selectedSku?.sku && (
          <Chip
            label={`SKU: ${selectedSku.sku}`}
            size='small'
            variant='outlined'
            color='primary'
            style={{ fontWeight: 600, width: '100%' }}
          />
        )}
        <Chip
          label={`Posición: ${positionCode}`}
          size='small'
          variant='outlined'
          color='primary'
          style={{ fontWeight: 600, width: '100%' }}
        />
      </Box>

      {/* Lotes vigentes */}
      {batchList.length > 0 && (
        <Box>
          <Typography
            variant='caption'
            style={{
              color: '#999',
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: 6
            }}
          >
            Lotes vigentes
          </Typography>

          {/* Cabecera tabla */}
          {showHeaders && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                backgroundColor: '#1B4AA0',
                borderRadius: '8px 8px 0 0',
                padding: '8px 12px'
              }}
            >
              {skuFefoType.hasBatchNumber && (
                <Typography
                  variant='caption'
                  style={{ color: '#fff', fontWeight: 700, letterSpacing: 0.5 }}
                >
                  LOTE
                </Typography>
              )}
              {skuFefoType.hasExpirationDate && (
                <Typography
                  variant='caption'
                  style={{ color: '#fff', fontWeight: 700, letterSpacing: 0.5 }}
                >
                  VENCIMIENTO
                </Typography>
              )}
            </Box>
          )}

          {/* Filas */}
          <Box
            style={{
              border: '1px solid #e5e7eb',
              borderTop: showHeaders ? 'none' : '1px solid #e5e7eb',
              borderRadius: showHeaders ? '0 0 8px 8px' : 8,
              padding: '0 12px',
              marginBottom: 12
            }}
          >
            {batchList.map((batch) => (
              <BatchRow
                key={batch.batchId}
                batch={batch}
                skuFefoType={skuFefoType}
                skuProductOriginDays={skuProductOriginDays}
                onClick={() => handleSelectBatch(batch.batchId, batchList)}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Lotes adicionales desde API */}
      {isExpanded && (
        <Box style={{ marginBottom: 12 }}>
          <Typography
            variant='caption'
            style={{
              color: '#6b7280',
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: 6
            }}
          >
            Otros lotes disponibles
          </Typography>

          {/* Búsqueda por número de lote */}
          <TextField
            size='small'
            fullWidth
            placeholder='Escanear lote...'
            value={searchBatch}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
            style={{ marginBottom: 10 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <Typography variant='body2' style={{ color: '#9ca3af' }}>
                      🔍
                    </Typography>
                  </InputAdornment>
                )
              }
            }}
          />

          {showHeaders && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                backgroundColor: '#1B4AA0',
                borderRadius: '8px 8px 0 0',
                padding: '8px 12px'
              }}
            >
              {skuFefoType.hasBatchNumber && (
                <Typography
                  variant='caption'
                  style={{ color: '#fff', fontWeight: 700, letterSpacing: 0.5 }}
                >
                  LOTE
                </Typography>
              )}
              {skuFefoType.hasExpirationDate && (
                <Typography
                  variant='caption'
                  style={{ color: '#fff', fontWeight: 700, letterSpacing: 0.5 }}
                >
                  VENCIMIENTO
                </Typography>
              )}
            </Box>
          )}

          {state.status === Status.Loading ? (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px 0'
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : state.status === Status.Success && uniqueApiItems.length > 0 ? (
            <>
              <Box
                style={{
                  border: '1px solid #d1d5db',
                  borderTop: showHeaders ? 'none' : '1px solid #d1d5db',
                  borderRadius: showHeaders ? '0 0 8px 8px' : 8,
                  padding: '0 12px',
                  marginBottom: 8,
                  backgroundColor: '#f9fafb'
                }}
              >
                {uniqueApiItems.map((batch) => (
                  <BatchRow
                    key={batch.batchId}
                    batch={batch}
                    skuFefoType={skuFefoType}
                    skuProductOriginDays={skuProductOriginDays}
                    onClick={() => handleSelectBatch(batch.batchId, uniqueApiItems)}
                  />
                ))}
              </Box>
              <Pagination
                pagination={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  totalItem: adjustedTotalItems
                }}
                onPaginationChange={handlePaginationChange}
              />
            </>
          ) : hasNoMoreBatches ? (
            <Typography
              variant='caption'
              style={{
                display: 'block',
                textAlign: 'center',
                color: '#9ca3af',
                padding: '12px 0'
              }}
            >
              No hay más lotes disponibles
            </Typography>
          ) : null}
        </Box>
      )}

      {/* Botones */}
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {isExpanded ? (
          <LoadingButton
            loading={false}
            variant='outlined'
            color='primary'
            fullWidth
            onClick={handleCollapse}
            style={{ borderRadius: 24, textTransform: 'none', fontWeight: 700 }}
          >
            − Mostrar menos
          </LoadingButton>
        ) : (
          <LoadingButton
            loading={state.status === Status.Loading}
            variant='contained'
            color='primary'
            fullWidth
            onClick={handleExpand}
            style={{ borderRadius: 24, textTransform: 'none', fontWeight: 700 }}
          >
            + Más Lotes
          </LoadingButton>
        )}

        <LoadingButton
          loading={false}
          variant='outlined'
          color='inherit'
          fullWidth
          onClick={handleCloseModal}
          style={{ borderRadius: 24, textTransform: 'none' }}
        >
          Cancelar
        </LoadingButton>
      </Box>
    </Fragment>
  )
}
